import React from 'react';
import { Marker } from '@react-google-maps/api';
import idleDrone from '@src/assets/drone-icons/idle.svg';
import idleDroneArrow from '@src/assets/drone-icons/idle-drone.svg';
import idleDroneSelected from '@src/assets/drone-icons/idle-selected.svg';
import missionDroneArrow from '@src/assets/drone-icons/mission-drone.svg';
import missionDroneSelected from '@src/assets/drone-icons/mission-selected.svg';
import errorDrone from '@src/assets/drone-icons/error.svg';
import defaultDrone from '@src/assets/drone-icons/default.svg';

import { ArcDroneMarkerProps, ArcDroneMarkerVariant } from './types';

const ArcDroneMarker: React.FC<ArcDroneMarkerProps> & ArcDroneMarkerVariant = (props) => {
  const { ...rest } = props;
  return <Marker {...rest} />;
};

ArcDroneMarker.idle = (props) => (
  <ArcDroneMarker 
    icon={{
      url: idleDrone,
      scaledSize: new window.google.maps.Size(50, 50),
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(25, 25),
    }}  
    {...props} />
);

ArcDroneMarker.idleArrow = (props) => (
  <ArcDroneMarker 
    icon={{
      url: idleDroneArrow,
      scaledSize: new window.google.maps.Size(42, 42), // match height
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(25, 25),
    }}  
    {...props} />
);


ArcDroneMarker.idleSelected = (props) => (
  <ArcDroneMarker 
    icon={{
      url: idleDroneSelected,
      scaledSize: new window.google.maps.Size(42, 42),
      origin: new window.google.maps.Point(0, 0),
      anchor: new window.google.maps.Point(25, 25),
    }}  
    {...props} />
);

ArcDroneMarker.missionArrow = (props: ArcDroneMarkerProps) => {
  const { rotation, position, ...restProps } = props;
  
  const rotatedSvgUrl = React.useMemo(() => {
    if (typeof rotation !== 'number' || !position) return missionDroneArrow;
    
    const svgContent = `
      <svg width="42" height="53" viewBox="0 0 42 53" xmlns="http://www.w3.org/2000/svg">
        <g transform="rotate(${rotation} 21 26.5)">
          <path fill="#35B7F3" opacity="0.5" d="M21 10.5a21 21 0 1 1 0 42 21 21 0 0 1 0-42z"/>
          <circle cx="21" cy="31.5" r="16" fill="#35B7F3"/>
          <path fill="#fff" d="M15.6 33.16c.33-.06.6.21.6.54 0 .33-.27.59-.59.69a2.1 2.1 0 0 0-1.41 1.9 2.1 2.1 0 0 0 4.04.59c.1-.32.36-.59.7-.59.33 0 .6.27.54.6a3.3 3.3 0 0 1-3.14 2.61 3.3 3.3 0 0 1-3.2-3.2 3.3 3.3 0 0 1 2.46-3.14zm10.8 0a3.3 3.3 0 0 1 2.46 3.14 3.3 3.3 0 0 1-3.2 3.2 3.3 3.3 0 0 1-3.15-2.6c-.06-.34.21-.61.54-.61.33 0 .6.27.7.6a2.1 2.1 0 0 0 4.04-.6 2.1 2.1 0 0 0-1.42-1.9c-.32-.1-.6-.36-.6-.7 0-.33.28-.6.63-.54zM25.16 26.23c.24-.24.62-.24.86 0 .24.24.24.63 0 .87l-2.47 2.48A3.17 3.17 0 0 1 24.2 31.5a3.17 3.17 0 0 1-.52 1.74l2.32 2.32c.24.24.24.63 0 .87a.6.6 0 0 1-.86 0l-2.3-2.3c-.52.36-1.17.57-1.96.57a3.17 3.17 0 0 1-1.95-.57l-2.38 2.38a.6.6 0 0 1-.86 0 .6.6 0 0 1 0-.86l2.43-2.43A3.17 3.17 0 0 1 17.8 31.5c0-.68.22-1.3.59-1.85l-2.59-2.59a.6.6 0 0 1 0-.86.6.6 0 0 1 .86 0l2.59 2.58c.52-.35 1.17-.57 1.95-.57.6 0 1.18.16 1.67.45l2.39-2.39zM16.2 23.5c1.56 0 2.86 1.12 3.15 2.6.06.33-.21.6-.54.6-.33 0-.6-.27-.7-.6a2.1 2.1 0 0 0-4.04.6c0 1 .67 1.86 1.59 2.14.33.1.6.36.6.68s-.27.6-.6.54A3.3 3.3 0 0 1 13 26.7c0-1.76 1.43-3.2 3.2-3.2zm9.6 0c1.77 0 3.2 1.43 3.2 3.2 0 1.76-1.22 3.03-2.7 3.3-.33.06-.6-.21-.6-.54 0-.32.27-.59.6-.69a2.1 2.1 0 0 0 1.42-1.9c0-1.05-.85-1.9-1.94-1.9-.92 0-1.67.59-1.95 1.49-.1.33-.36.59-.68.59-.33 0-.6-.27-.54-.6.29-1.48 1.59-2.6 3.15-2.6z"/>
          <path d="M27.92 8.67A27.7 27.7 0 0 0 21 7.5c-2.43 0-4.76.41-6.93 1.17L21 0l6.92 8.67z" fill="#35B7F3"/>
        </g>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgContent)))}`;
  }, [rotation, position]);
  
  const iconUrl = typeof rotation === 'number' && position ? rotatedSvgUrl : missionDroneArrow;
  
  return (
    <ArcDroneMarker 
      icon={{
        url: iconUrl,
        scaledSize: new window.google.maps.Size(52.5, 52.5),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(25, 25),
      }}
      position={position}
      {...restProps} />
  );
};


ArcDroneMarker.missionSelected = (props: ArcDroneMarkerProps) => {
  const { rotation, position, ...restProps } = props;

  const rotatedSvgUrl = React.useMemo(() => {
    if (typeof rotation !== "number" || !position) return missionDroneSelected;

    const svgContent = `
      <svg
        width="42"
        height="53"
        viewBox="0 0 42 53"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g transform="rotate(${rotation} 21 26.5)">

          <!-- внешний полупрозрачный круг -->
          <path
            fill="#35B7F3"
            opacity="0.5"
            d="M21 10.5a21 21 0 1 1 0 42 21 21 0 0 1 0-42z"
          />

          <circle
            cx="21"
            cy="31.5"
            r="15.5"
            fill="#35B7F3"
            stroke="white"
            stroke-width="1"
          />

          <path
            fill="#fff"
            d="M15.6 33.16c.33-.06.6.21.6.54 0 .33-.27.59-.59.69a2.1 2.1 0 0 0-1.41 1.9 2.1 2.1 0 0 0 4.04.59c.1-.32.36-.59.7-.59.33 0 .6.27.54.6a3.3 3.3 0 0 1-3.14 2.61 3.3 3.3 0 0 1-3.2-3.2 3.3 3.3 0 0 1 2.46-3.14zm10.8 0a3.3 3.3 0 0 1 2.46 3.14 3.3 3.3 0 0 1-3.2 3.2 3.3 3.3 0 0 1-3.15-2.6c-.06-.34.21-.61.54-.61.33 0 .6.27.7.6a2.1 2.1 0 0 0 4.04-.6 2.1 2.1 0 0 0-1.42-1.9c-.32-.1-.6-.36-.6-.7 0-.33.28-.6.63-.54zM25.16 26.23c.24-.24.62-.24.86 0 .24.24.24.63 0 .87l-2.47 2.48A3.17 3.17 0 0 1 24.2 31.5a3.17 3.17 0 0 1-.52 1.74l2.32 2.32c.24.24.24.63 0 .87a.6.6 0 0 1-.86 0l-2.3-2.3c-.52.36-1.17.57-1.96.57a3.17 3.17 0 0 1-1.95-.57l-2.38 2.38a.6.6 0 0 1-.86 0 .6.6 0 0 1 0-.86l2.43-2.43A3.17 3.17 0 0 1 17.8 31.5c0-.68.22-1.3.59-1.85l-2.59-2.59a.6.6 0 0 1 0-.86.6.6 0 0 1 .86 0l2.59 2.58c.52-.35 1.17-.57 1.95-.57.6 0 1.18.16 1.67.45l2.39-2.39zM16.2 23.5c1.56 0 2.86 1.12 3.15 2.6.06.33-.21.6-.54.6-.33 0-.6-.27-.7-.6a2.1 2.1 0 0 0-4.04.6c0 1 .67 1.86 1.59 2.14.33.1.6.36.6.68s-.27.6-.6.54A3.3 3.3 0 0 1 13 26.7c0-1.76 1.43-3.2 3.2-3.2zm9.6 0c1.77 0 3.2 1.43 3.2 3.2 0 1.76-1.22 3.03-2.7 3.3-.33.06-.6-.21-.6-.54 0-.32.27-.59.6-.69a2.1 2.1 0 0 0 1.42-1.9c0-1.05-.85-1.9-1.94-1.9-.92 0-1.67.59-1.95 1.49-.1.33-.36.59-.68.59-.33 0-.6-.27-.54-.6.29-1.48 1.59-2.6 3.15-2.6z"
          />

          <path
            d="M27.92 8.67A27.7 27.7 0 0 0 21 7.5c-2.43 0-4.76.41-6.93 1.17L21 0l6.92 8.67z"
            fill="#35B7F3"
          />

        </g>
      </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(
      unescape(encodeURIComponent(svgContent))
    )}`;
  }, [rotation, position]);

  return (
    <ArcDroneMarker
      icon={{
        url: rotatedSvgUrl,
        scaledSize: new window.google.maps.Size(52.5, 52.5),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(25, 25),
      }}
      position={position}
      {...restProps}
    />
  );
};


ArcDroneMarker.error = (props) => (
  <ArcDroneMarker icon={{
    url: errorDrone,
    scaledSize: new window.google.maps.Size(50, 50),
    origin: new window.google.maps.Point(0, 0),
    anchor: new window.google.maps.Point(25, 25),
  }} 
  {...props} />
);

ArcDroneMarker.default = (props) => (
  <ArcDroneMarker icon={{
    url: defaultDrone,
    scaledSize: new window.google.maps.Size(50, 50),
    origin: new window.google.maps.Point(0, 0),
    anchor: new window.google.maps.Point(25, 25),
  }} 
  {...props} />
);

export default ArcDroneMarker;